import React, { useState, useEffect } from "react";
import { Dialog } from '../dialogs/dialog';
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Slider } from "primereact/slider";
import { useThemesData } from "../../providers/theme-management-provider";
import {  ColorIntf, parseColor } from "../../general/utilities";

interface InputsConfig {
    inputBg: ColorIntf;
    inputText: ColorIntf;
    buttonBg: ColorIntf;
    buttonText: ColorIntf;
}

export const InputsDialog: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => {
    const { findCssValue, bulkUpdateCssProperties } = useThemesData();

    const [config, setConfig] = useState<InputsConfig>({
        inputBg: parseColor("#ffffff"),
        inputText: parseColor("#333333"),
        buttonBg: parseColor("#3b82f6"),
        buttonText: parseColor("#ffffff"),
    });

    useEffect(() => {
        if (visible) {
            setConfig({
                inputBg: parseColor(findCssValue(".p-inputtext", "background") || "#ffffff"),
                inputText: parseColor(findCssValue(".p-inputtext", "color") || "#333333"),
                buttonBg: parseColor(findCssValue(".p-button", "background") || "#3b82f6"),
                buttonText: parseColor(findCssValue(".p-button", "color") || "#ffffff"),
            });
        }
    }, [visible, findCssValue]);

    const toHex = (c: ColorIntf) => 
        `#${((1 << 24) + (c.r << 16) + (c.g << 8) + c.b).toString(16).slice(1)}`;

    const updateColor = (key: keyof InputsConfig, newHex: string) => {
        const hex: string = newHex.startsWith('#') ? newHex : `#${newHex}`;
        const parsed = parseColor(hex);
        setConfig(prev => ({ 
            ...prev, 
            [key]: { ...parsed, a: (prev[key] as ColorIntf).a } 
        }));
    };

    const updateAlpha = (key: keyof InputsConfig, newAlpha: number) => {
        setConfig(prev => ({
            ...prev,
            [key]: { ...(prev[key] as ColorIntf), a: newAlpha / 100 }
        }));
    };

    const handleApply = (): void => {
        const serialize = (c: ColorIntf) => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

        const bg = serialize(config.inputBg);
        const text = serialize(config.inputText);
        const bBg = serialize(config.buttonBg);
        const bText = serialize(config.buttonText);

        const updates = [
            { s: ".p-inputtext", p: [{ property: "background", value: bg }, { property: "color", value: text }] },
            { s: ".p-dropdown", p: [{ property: "background", value: bg }, { property: "color", value: text }] },
            { s: ".p-dropdown-panel", p: [{ property: "background", value: bg }, { property: "color", value: text }] },
            { s: ".p-checkbox", p: [{ property: "background", value: bg }] },
            { s: ".p-checkbox-box", p: [{ property: "background", value: bg }] },
            { s: ".p-checkbox .p-checkbox-box", p: [{ property: "background", value: bg }] },
            { s: ".p-radiobutton", p: [{ property: "background", value: bg }] },
            { s: ".p-radiobutton-box", p: [{ property: "background", value: bg }] },
            { s: ".p-listbox", p: [{ property: "background", value: bg }, { property: "color", value: text }] },
            { s: ".p-button", p: [{ property: "background", value: bBg }, { property: "color", value: bText }] }
        ];

        updates.forEach(u => bulkUpdateCssProperties(u.s, u.p, 'merge'));
        onHide();
    };

    const renderColorRow = (label: string, configKey: keyof InputsConfig) => {
        const color = config[configKey] as ColorIntf;
        return (
            <div className="flex align-items-center mb-2">
                {/* Main Label: Fixed width, left aligned */}
                <label style={{ width: '120px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {label}:
                </label>
                
                {/* Right Aligned Wrapper: margin-left: auto pushes everything to the right */}
                <div className="flex align-items-center" style={{ marginLeft: 'auto', gap: '8px' }}>
                    {/* Color Picker: Fixed width 64px */}
                    <div style={{ width: '64px', flexShrink: 0 }}>
                        <ColorPicker 
                            value={toHex(color)} 
                            onChange={(e) => {
                                updateColor(configKey, e.value as string);
                            }} 
                        />
                    </div>
                    <span className="text-sm font-semibold">Tarnsparency:</span>
                    <Slider 
                        value={color.a * 100} 
                        min={0} max={100} 
                        onChange={(e) => updateAlpha(configKey, e.value as number)} 
                        style={{ width: '150px' }} 
                    />
                </div>
            </div>
        );
    };

    return (
        <Dialog header="Input elements" visible={visible} onHide={onHide} style={{ width: '1000px' }}>
            <div className="grid grid-nogutter p-fluid">
                <div className="col-6 pr-3">
                    {renderColorRow("Text color", "inputText")}
                    {renderColorRow("Button text", "buttonText")}
                </div>
                <div className="col-6 pl-3">
                    {renderColorRow("Background", "inputBg")}
                    {renderColorRow("Button background", "buttonBg")}
                </div>
            </div>
            <div className="admin-toolbar flex justify-content-end mt-4 gap-2">
                <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-secondary" />
                <Button label="Apply" icon="pi pi-check" onClick={handleApply} className="p-button-primary" />
            </div>
        </Dialog>
    );
}
